import {
  type FixedDepartureCategoryOut,
  type FixedDepartureDetail,
  type FixedDeparturePaxSlabKind,
  type SaveFixedDepartureCategoryPricesRequest,
} from '@utazzo/shared';
import { UzButton, UzEmptyState, UzInput } from '@utazzo/ui';
import { LayoutGrid, Plus, Trash2 } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import {
  useCreateFixedDepartureCategory,
  useDeleteFixedDepartureCategory,
  useSaveFixedDepartureCategoryPrices,
  useUpdateFixedDepartureCategory,
} from '../use-fixed-departures';

/**
 * Tiers + the price grid - the shape a supplier rate sheet actually arrives in.
 *
 * Columns are grade tiers ("Standard", "Premium", "Luxury"), rows are pax
 * slabs (02/04/06/… adults, then Extra Bed and Child without Bed). Every cell
 * is stored: the curve is non-monotonic (06 pax can be cheaper than 08, since
 * the included vehicle steps up), so nothing here is derivable.
 *
 * Values are typed in RUPEES and stored as paise.
 */

/** The adult group ladder a domestic sheet usually quotes, as exact sizes. */
const DEFAULT_ADULT_SLABS: Band[] = [
  [2, 2],
  [4, 4],
  [6, 6],
  [8, 8],
  [10, 10],
  [12, 12],
];

/** Inclusive group-size band. An exact size is just `[n, n]`. */
type Band = [number, number];

/** The per-head kinds, in the order sheets print them. */
const EXTRA_ROWS: { key: string; label: string; kind: FixedDeparturePaxSlabKind }[] = [
  { key: 'single', label: 'Single Supplement', kind: 'SINGLE_SUPPLEMENT' },
  { key: 'extra', label: 'Extra Bed', kind: 'EXTRA_BED' },
  { key: 'childbed', label: 'Child with Bed', kind: 'CHILD_WITH_BED' },
  { key: 'child', label: 'Child without Bed', kind: 'CHILD_WITHOUT_BED' },
  { key: 'infant', label: 'Infant', kind: 'INFANT' },
];

interface Row {
  key: string;
  label: string;
  kind: FixedDeparturePaxSlabKind;
  paxFrom: number;
  paxTo: number;
}

const bandLabel = ([from, to]: Band): string =>
  from === to ? `${String(from).padStart(2, '0')} Adults` : `${from}-${to} Pax`;

const buildRows = (adultSlabs: Band[]): Row[] => [
  ...adultSlabs.map((b) => ({
    key: `adult:${b[0]}-${b[1]}`,
    label: bandLabel(b),
    kind: 'ADULT_SLAB' as const,
    paxFrom: b[0],
    paxTo: b[1],
  })),
  ...EXTRA_ROWS.map((r) => ({ ...r, paxFrom: 0, paxTo: 0 })),
];

const cellKey = (categoryId: string, row: Row): string => `${categoryId}|${row.key}`;

/** Rupee string for an input; blank when the cell has no price. */
const paiseToInput = (paise: string | undefined): string =>
  paise === undefined ? '' : String(Number(paise) / 100);

const inputToPaise = (value: string): string => {
  const n = Number(value.replace(/,/g, '').trim());
  return Number.isFinite(n) ? String(Math.round(n * 100)) : '0';
};

/** Adult bands already present in the saved data, so a custom ladder survives a reload. */
const adultSlabsFrom = (categories: ReadonlyArray<FixedDepartureCategoryOut>): Band[] => {
  const found = new Map<string, Band>();
  categories.forEach((c) =>
    c.prices
      .filter((p) => p.kind === 'ADULT_SLAB')
      .forEach((p) => found.set(`${p.paxFrom}-${p.paxTo}`, [p.paxFrom, p.paxTo])),
  );
  if (found.size === 0) return DEFAULT_ADULT_SLABS;
  return [...found.values()].sort((a, b) => a[0] - b[0]);
};

export const FdPricingStep = ({ fd }: { fd: FixedDepartureDetail }): React.JSX.Element => {
  const createCategory = useCreateFixedDepartureCategory(fd.id);
  const updateCategory = useUpdateFixedDepartureCategory(fd.id);
  const deleteCategory = useDeleteFixedDepartureCategory(fd.id);
  const savePrices = useSaveFixedDepartureCategoryPrices(fd.id);

  const [adultSlabs, setAdultSlabs] = useState<Band[]>(() => adultSlabsFrom(fd.categories));
  const [newSlab, setNewSlab] = useState('');
  const [newTier, setNewTier] = useState('');
  const [cells, setCells] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  const rows = useMemo(() => buildRows(adultSlabs), [adultSlabs]);

  // Re-seed the grid from the server whenever the template reloads.
  useEffect(() => {
    setAdultSlabs(adultSlabsFrom(fd.categories));
    const next: Record<string, string> = {};
    fd.categories.forEach((c) => {
      c.prices.forEach((p) => {
        const rowKey =
          p.kind === 'ADULT_SLAB'
            ? `adult:${p.paxFrom}-${p.paxTo}`
            : (EXTRA_ROWS.find((r) => r.kind === p.kind)?.key ?? p.kind);
        next[`${c.id}|${rowKey}`] = paiseToInput(p.pricePaise);
      });
    });
    setCells(next);
  }, [fd.categories]);

  /** Accepts an exact size ("8") or a band ("8-9"), matching how sheets print them. */
  const addSlab = (): void => {
    const raw = newSlab.trim();
    const m = /^(\d{1,2})(?:\s*-\s*(\d{1,2}))?$/.exec(raw);
    if (!m) return;
    const from = Number(m[1]);
    const to = m[2] === undefined ? from : Number(m[2]);
    if (from < 1 || to < from || to > 99) return;
    if (adultSlabs.some((b) => b[0] === from && b[1] === to)) return;
    setAdultSlabs((prev) => [...prev, [from, to] as Band].sort((a, b) => a[0] - b[0]));
    setNewSlab('');
  };

  const removeSlab = (band: Band): void =>
    setAdultSlabs((prev) =>
      prev.length > 1 ? prev.filter((b) => !(b[0] === band[0] && b[1] === band[1])) : prev,
    );

  const addTier = async (): Promise<void> => {
    const name = newTier.trim();
    if (!name) return;
    await createCategory.mutateAsync({ name });
    setNewTier('');
  };

  /** Saves one tier's whole column; blank and zero cells are simply omitted. */
  const saveColumn = async (category: FixedDepartureCategoryOut): Promise<void> => {
    const prices: SaveFixedDepartureCategoryPricesRequest['prices'] = rows
      .map((row) => ({ row, raw: cells[cellKey(category.id, row)] ?? '' }))
      .filter(({ raw }) => raw.trim() !== '')
      .map(({ row, raw }) => ({
        kind: row.kind,
        paxFrom: row.paxFrom,
        paxTo: row.paxTo,
        pricePaise: inputToPaise(raw),
      }));
    setSavingId(category.id);
    try {
      await savePrices.mutateAsync({ categoryId: category.id, payload: { prices } });
    } finally {
      setSavingId(null);
    }
  };

  if (fd.categories.length === 0) {
    return (
      <div className="space-y-4">
        <UzEmptyState
          icon={<LayoutGrid className="h-6 w-6" aria-hidden />}
          title="No grade tiers yet"
          description="A vendor rate sheet quotes the same trip at several tiers (Standard, Deluxe, Premium, …), each with its own hotels and its own price column. Add the first tier to start the grid."
        />
        <div className="mx-auto flex max-w-md items-center gap-2">
          <UzInput
            className="flex-1"
            placeholder="e.g. Standard"
            value={newTier}
            onChange={(e) => setNewTier(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void addTier();
            }}
          />
          <UzButton onClick={addTier} disabled={!newTier.trim()} loading={createCategory.isPending}>
            <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            Add tier
          </UzButton>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-zinc-900">Tiers and price grid</h2>
          <p className="mt-0.5 text-xs text-zinc-500">
            Cost per person, in rupees. Leave a cell blank if the vendor does not quote it. Each
            column saves on its own.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <UzInput
            className="w-44"
            placeholder="New tier name"
            value={newTier}
            onChange={(e) => setNewTier(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void addTier();
            }}
          />
          <UzButton
            size="sm"
            variant="outline"
            onClick={addTier}
            disabled={!newTier.trim()}
            loading={createCategory.isPending}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            Add tier
          </UzButton>
        </div>
      </div>

      {/* The grid scrolls inside its own box - 7 tiers is wider than the page. */}
      <div className="overflow-x-auto rounded-lg border border-zinc-200 bg-white">
        <table className="w-full min-w-max border-collapse text-sm">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50/70">
              <th className="sticky left-0 z-10 bg-zinc-50/70 px-4 py-3 text-left text-xs font-semibold tracking-wide text-zinc-500 uppercase">
                Cost per person
              </th>
              {fd.categories.map((category) => (
                <th key={category.id} className="min-w-[150px] px-3 py-2 text-left align-top">
                  <UzInput
                    className="h-8 text-xs font-semibold"
                    value={category.name}
                    onChange={(e) =>
                      updateCategory.mutate({
                        categoryId: category.id,
                        payload: { name: e.target.value },
                      })
                    }
                  />
                  <div className="mt-1 flex items-center justify-between gap-1">
                    <span className="text-[10px] text-zinc-400">
                      {category.prices.length} priced
                    </span>
                    <button
                      type="button"
                      className="text-zinc-300 transition-colors hover:text-red-600"
                      onClick={() => deleteCategory.mutate(category.id)}
                      aria-label={`Remove ${category.name}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="border-b border-zinc-100 last:border-0">
                <th className="sticky left-0 z-10 bg-white px-4 py-2 text-left text-xs font-medium whitespace-nowrap text-zinc-700">
                  <span className="flex items-center gap-2">
                    {row.label}
                    {row.kind === 'ADULT_SLAB' && adultSlabs.length > 1 && (
                      <button
                        type="button"
                        className="text-zinc-300 transition-colors hover:text-red-600"
                        onClick={() => removeSlab([row.paxFrom, row.paxTo])}
                        aria-label={`Remove the ${row.label} row`}
                      >
                        <Trash2 className="h-3 w-3" aria-hidden />
                      </button>
                    )}
                  </span>
                </th>
                {fd.categories.map((category) => (
                  <td key={category.id} className="px-3 py-1.5">
                    <UzInput
                      className="h-8 text-right tabular-nums"
                      inputMode="decimal"
                      placeholder="-"
                      value={cells[cellKey(category.id, row)] ?? ''}
                      onChange={(e) =>
                        setCells((prev) => ({
                          ...prev,
                          [cellKey(category.id, row)]: e.target.value,
                        }))
                      }
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-zinc-200 bg-zinc-50/40">
              <td className="sticky left-0 z-10 bg-zinc-50/40 px-4 py-2">
                <div className="flex items-center gap-1.5">
                  <UzInput
                    className="h-8 w-24"
                    inputMode="text"
                    placeholder="8 or 8-9"
                    value={newSlab}
                    onChange={(e) => setNewSlab(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') addSlab();
                    }}
                  />
                  <UzButton size="sm" variant="ghost" onClick={addSlab} disabled={!newSlab.trim()}>
                    <Plus className="h-3.5 w-3.5" aria-hidden />
                  </UzButton>
                </div>
              </td>
              {fd.categories.map((category) => (
                <td key={category.id} className="px-3 py-2">
                  <UzButton
                    size="sm"
                    className="w-full"
                    loading={savingId === category.id}
                    onClick={() => saveColumn(category)}
                  >
                    Save
                  </UzButton>
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};
